import React, { useState, useEffect } from 'react';
import './K12Preview.css';

const K12_CAMPS = [
  {
    name: 'Roblox Coding Adventure',
    ageRange: '6–10',
    difficulty: 'beginner',

    color: '#e03e2d',
    emoji: '🎮',
    tagline: 'Build your own Roblox games — and learn to code while you play!',
    desc: 'Use Roblox Studio to design levels, write your first Lua scripts, and share games your friends can play. No experience needed!',
    images: [
      { softBg: 'linear-gradient(180deg, #ffe4e1 0%, #ffc9c4 100%)', icon: '🏗️', caption: 'Build Cool Worlds', desc: 'Design fun levels, obstacles, and secret rooms' },
      { softBg: 'linear-gradient(180deg, #fff3cd 0%, #ffe89e 100%)', icon: '✨', caption: 'Add Magic with Scripts', desc: 'Make doors open, coins sparkle, and things move!' },
      { softBg: 'linear-gradient(180deg, #d1f7e7 0%, #a7f3d0 100%)', icon: '🚀', caption: 'Share Your Game', desc: 'Publish and play with friends — for real!' },
    ],
    featuredProject: {
      title: 'Build & Launch Your Own Roblox Obby',
      desc: 'Design a full obstacle course game in Roblox Studio — add traps, power-ups, and a finish line. Then publish it for friends to play!',
      bullets: ['Custom Levels', 'Lua Script', 'Live Game Link'],
    },
    activityCopy: 'Try some questions from the program — just like students do in class! Pick what you know or give it your best guess.',
    reassurance: [
      { title: 'Super Beginner Friendly', desc: 'Zero experience needed — we start from scratch!' },
      { title: 'Learn by Playing', desc: 'Build real Roblox games as you learn' },
      { title: 'Real Coding Skills', desc: 'Lua powers Roblox AND other cool games' },
    ],
    interests: ['Gaming', 'Building', 'Creativity', 'Coding', 'Fun'],
    quiz: {
      concept: [
        {
          question: 'What is Roblox Studio?',
          options: [
            'A place to buy Robux',
            'A special app where you build and make your own Roblox games',
            'A website for watching Roblox videos',
            'A game where you fight other players',
          ],
          answer: 1,
        },
        {
          question: 'What is a "Part" in Roblox Studio?',
          options: [
            'A type of Roblox character skin',
            'A building block you use to make walls, floors, and objects',
            'A song that plays in your game',
            'A leaderboard for scores',
          ],
          answer: 1,
        },
        {
          question: 'What language do you use to write scripts in Roblox?',
          options: [
            'Python',
            'English words only',
            'Lua',
            'HTML',
          ],
          answer: 2,
        },
        {
          question: 'What is a "Spawn Point" in a Roblox game?',
          options: [
            'A secret treasure chest',
            'The place where your character appears when the game starts',
            'A button that turns music on',
            'A special hat for your avatar',
          ],
          answer: 1,
        },
        {
          question: 'What does a script do in Roblox?',
          options: [
            'It changes the color of the sky only',
            'It makes things in your game move, react, and do cool stuff',
            'It lets you buy new clothes for your character',
            'It speeds up your internet connection',
          ],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'You want to make a brick disappear when a player touches it. Where do you put the script?',
          options: [
            'Inside the player\'s backpack',
            'In the Sky box',
            'Inside the brick Part in Roblox Studio',
            'On the leaderboard',
          ],
          answer: 2,
        },
        {
          question: 'You built a long hallway in your game but it floats in the air. How do you fix it?',
          options: [
            'Delete the whole hallway and start over',
            'Use the Move tool to drag it down so it sits on the ground',
            'Change its color to blue',
            'Add a script that says "go down"',
          ],
          answer: 1,
        },
        {
          question: 'Your obby is too hard — players keep falling off right away. What is the best fix?',
          options: [
            'Make the starting platforms bigger and easier to jump on',
            'Remove all the platforms',
            'Make the game private so nobody can play',
            'Change the background music',
          ],
          answer: 0,
        },
        {
          question: 'You want a coin to give a player 10 points when they pick it up. Which step comes first?',
          options: [
            'Publish the game to everyone',
            'Place a Part shaped like a coin and add a script to detect when it is touched',
            'Change the sky color to gold',
            'Add a leaderboard image',
          ],
          answer: 1,
        },
        {
          question: 'You finished building your game and want your friends to play it. What do you do?',
          options: [
            'Email Roblox and ask them to add it',
            'Print the game out on paper',
            'Click Publish to Roblox so it goes live and share the link',
            'Save it only on your computer',
          ],
          answer: 2,
        },
      ],
    },
  },
  {
    name: 'Movie Making with Scratch',
    ageRange: '6–10',
    difficulty: 'beginner',

    color: '#7c3aed',
    emoji: '🎬',
    tagline: 'Lights, camera, code! Make your own animated movies in Scratch.',
    desc: 'Use Scratch to create characters, build movie scenes, add voices and sound effects, and share your very own animated film with the world!',
    images: [
      { softBg: 'linear-gradient(180deg, #ede9fe 0%, #ddd6fe 100%)', icon: '🎭', caption: 'Create Your Characters', desc: 'Design sprites and costumes for your movie cast' },
      { softBg: 'linear-gradient(180deg, #fce7f3 0%, #fbcfe8 100%)', icon: '🎥', caption: 'Build Movie Scenes', desc: 'Paint backdrops and direct your characters on screen' },
      { softBg: 'linear-gradient(180deg, #fef9c3 0%, #fef08a 100%)', icon: '🔊', caption: 'Add Sound & Voices', desc: 'Record narration, add music, and make it come alive!' },
    ],
    featuredProject: {
      title: 'Write, Animate & Share Your Own Short Film',
      desc: 'Students write a story, build all the scenes in Scratch, animate their characters speaking and moving, then share their finished movie with friends and family!',
      bullets: ['Original Story', 'Animated Scenes', 'Voice & Sound', 'Shareable Film Link'],
    },
    activityCopy: 'Try some fun questions from the program — just like real students do! Do your best and see what you know.',
    reassurance: [
      { title: 'No Typing Required', desc: 'Scratch uses colorful drag-and-drop blocks' },
      { title: 'Express Yourself', desc: 'Tell any story you can imagine' },
      { title: 'Real Coding Concepts', desc: 'Sequences, loops & events — taught through movies!' },
    ],
    interests: ['Movies', 'Storytelling', 'Art', 'Creativity', 'Coding'],
    quiz: {
      concept: [
        {
          question: 'What is Scratch?',
          options: [
            'A video game you buy at a store',
            'A free coding tool where you snap colorful blocks together to make projects',
            'A drawing app only for adults',
            'A type of computer chip',
          ],
          answer: 1,
        },
        {
          question: 'What is a "Sprite" in Scratch?',
          options: [
            'A fizzy drink you code with',
            'The background picture behind your scene',
            'A character or object in your Scratch project that can move and talk',
            'A block that plays music',
          ],
          answer: 2,
        },
        {
          question: 'What is a "Backdrop" in Scratch?',
          options: [
            'A type of costume for your sprite',
            'The background scene that appears behind your characters — like a movie set!',
            'A button that stops your project',
            'A sound effect block',
          ],
          answer: 1,
        },
        {
          question: 'What does clicking the green flag do in Scratch?',
          options: [
            'Deletes all your sprites',
            'Changes the backdrop color',
            'Starts your project from the beginning — like pressing Play!',
            'Saves your project to a USB drive',
          ],
          answer: 2,
        },
        {
          question: 'In coding, what is a "sequence"?',
          options: [
            'A special type of Scratch sprite',
            'Steps that happen one after another in the right order',
            'A kind of backdrop with stars',
            'A button that rewinds your movie',
          ],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'You want your cat sprite to say "Action!" at the start of your movie. Which block do you use?',
          options: [
            'Play sound block',
            'Say "Action!" for 2 seconds block under the Looks section',
            'Change backdrop block',
            'Stop all block',
          ],
          answer: 1,
        },
        {
          question: 'Your movie has two scenes — a beach and a castle. How do you switch from one scene to the other?',
          options: [
            'Delete the beach backdrop',
            'Use a "Switch backdrop to castle" block at the right moment in your code',
            'Draw the castle on top of the beach',
            'Start a brand new project for the castle scene',
          ],
          answer: 1,
        },
        {
          question: 'You want your robot sprite to walk across the screen. Which type of block moves it?',
          options: [
            'A Sound block',
            'A Looks block',
            'A Motion block like "move 10 steps"',
            'A Control block like "wait 1 second"',
          ],
          answer: 2,
        },
        {
          question: 'Your movie scene has three characters talking one after another. What is the best way to make sure they speak in the right order?',
          options: [
            'Run all three sprites at exactly the same time',
            'Put each sprite\'s "say" block in the correct sequence so they happen one after another',
            'Delete two of the sprites',
            'Use only one sprite for all the voices',
          ],
          answer: 1,
        },
        {
          question: 'You finished your Scratch movie and want grandma to watch it. What do you do?',
          options: [
            'Print the screen out on paper',
            'Mail your computer to grandma',
            'Share the Scratch project link so she can watch it in her browser',
            'Save it only on your computer and never open it again',
          ],
          answer: 2,
        },
      ],
    },
  },
  {
    name: 'Minecraft Madness',
    ageRange: '6–10',
    difficulty: 'beginner',

    color: '#ca8a04',
    emoji: '⛏️',
    tagline: 'Mine, build, and code your way to awesome Minecraft creations!',
    desc: 'Use Minecraft Education Edition to build incredible worlds, trigger cool events with Redstone, and write your very first code commands — all inside the game you already love!',
    images: [
      { softBg: 'linear-gradient(180deg, #fef9c3 0%, #fef08a 100%)', icon: '🏰', caption: 'Build Epic Structures', desc: 'Design castles, cities, and worlds block by block' },
      { softBg: 'linear-gradient(180deg, #fee2e2 0%, #fecaca 100%)', icon: '⚡', caption: 'Power Up with Redstone', desc: 'Make lights, doors, and traps come to life!' },
      { softBg: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)', icon: '💻', caption: 'Code Inside the Game', desc: 'Write commands and scripts that change your world' },
    ],
    featuredProject: {
      title: 'Design & Code Your Own Minecraft Adventure Map',
      desc: 'Students build a full adventure world in Minecraft Education Edition — complete with Redstone traps, a coded treasure hunt, and a story for other players to explore!',
      bullets: ['Custom World Build', 'Redstone Circuit', 'Code Commands', 'Playable Map'],
    },
    activityCopy: 'Try some questions from the program — just like students do in camp! Give it your best shot!',
    reassurance: [
      { title: 'Already Know Minecraft?', desc: 'Perfect — you\'ve got a huge head start!' },
      { title: 'Learn Real Coding', desc: 'Commands and scripts teach coding that works everywhere' },
      { title: 'Build Something Big', desc: 'Take home a world you designed yourself' },
    ],
    interests: ['Minecraft', 'Building', 'Gaming', 'Coding', 'Creativity'],
    quiz: {
      concept: [
        {
          question: 'What is Minecraft Education Edition?',
          options: [
            'A harder, more boring version of Minecraft',
            'A special version of Minecraft made for learning coding, science, and creativity in school',
            'A game where you can only watch other players build',
            'A Minecraft app only for teachers',
          ],
          answer: 1,
        },
        {
          question: 'What is Redstone in Minecraft?',
          options: [
            'A rare gem you trade for diamonds',
            'A special material that carries power to make things move, light up, or open — like electrical wire!',
            'A type of pickaxe upgrade',
            'A red-colored stone block used only for decoration',
          ],
          answer: 1,
        },
        {
          question: 'In coding, what does a "loop" do?',
          options: [
            'It deletes blocks you placed by mistake',
            'It makes your character jump higher',
            'It repeats a set of steps over and over so you don\'t have to write them again',
            'It changes the color of your world',
          ],
          answer: 2,
        },
        {
          question: 'What is a Command Block in Minecraft?',
          options: [
            'A block that breaks everything around it',
            'A special block you type instructions into to make things happen in your world',
            'A controller for flying',
            'A block that only admins can see',
          ],
          answer: 1,
        },
        {
          question: 'What does "debugging" mean when you are coding?',
          options: [
            'Squashing real bugs you find in the game',
            'Finding and fixing mistakes in your code so it works correctly',
            'Deleting all your code and starting over',
            'Adding more bugs to make the game harder',
          ],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'You want a door in your Minecraft world to open automatically when a player walks near it. What should you use?',
          options: [
            'A painting on the wall',
            'A Redstone pressure plate connected to the door',
            'A bucket of water',
            'A torch placed next to the door',
          ],
          answer: 1,
        },
        {
          question: 'Your Redstone lamp is not turning on even though it is connected to a lever. What should you check first?',
          options: [
            'Change the lamp to a different color',
            'Make sure the Redstone dust trail is connected all the way from the lever to the lamp with no gaps',
            'Remove the lever and try a button instead',
            'Dig the lamp underground',
          ],
          answer: 1,
        },
        {
          question: 'You want to place 10 trees in a row without placing each one by hand. Which coding idea helps you do that?',
          options: [
            'A variable',
            'A sound block',
            'A loop that repeats the "place tree" command 10 times',
            'A Redstone torch',
          ],
          answer: 2,
        },
        {
          question: 'You typed a command to summon a wolf but nothing happened. What is the most likely problem?',
          options: [
            'Wolves do not exist in Minecraft',
            'There is a spelling mistake or wrong word in your command',
            'You need to restart your computer first',
            'Commands only work at night in Minecraft',
          ],
          answer: 1,
        },
        {
          question: 'You built an adventure map and want your classmates to explore it. What do you do?',
          options: [
            'Take a photo of your screen and send it',
            'Describe the map out loud to your class',
            'Share the world file so classmates can open and play your map',
            'Build it again from scratch in front of everyone',
          ],
          answer: 2,
        },
      ],
    },
  },
  {
    name: 'Code & Create: Minecraft + Scratch',
    ageRange: '6–10',
    difficulty: 'beginner',

    color: '#43e97b',
    emoji: '⛏️',
    tagline: 'Learn real coding skills through Scratch animations and Minecraft Education challenges.',
    desc: 'A creative intro to coding for young learners — using the games and tools they already love.',
    images: [
      { softBg: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)', icon: '⛏️', caption: 'Minecraft Logic', desc: 'Code Builder challenges and logic puzzles' },
      { softBg: 'linear-gradient(180deg, #fce7f3 0%, #fbcfe8 100%)', icon: '🐱', caption: 'Scratch Animations', desc: 'Animate characters, stories, and sounds' },
      { softBg: 'linear-gradient(180deg, #ffedd5 0%, #fed7aa 100%)', icon: '🎮', caption: 'Game & Story Design', desc: 'Design games and interactive stories' },
    ],
    featuredProject: {
      title: 'Code Your Own Interactive Scratch Story',
      desc: 'Build an animated Scratch project with characters, movement, and sound.',
      bullets: ['Scratch Story', 'Animations', 'Logic Code'],
    },
    activityCopy: 'Try the kinds of coding challenges and creative questions students work through in class.',
    reassurance: [
      { title: 'Beginner Friendly', desc: 'Great for first-time coders' },
      { title: 'Project Based', desc: 'Code through play and games' },
      { title: 'Future Ready', desc: 'Logic + creative coding skills' },
    ],
    interests: ['Minecraft', 'Coding', 'Scratch', 'Games', 'Creativity'],
    quiz: {
      concept: [
        {
          question: 'What is Scratch?',
          options: ['A video game', 'A block-based coding program', 'A type of Minecraft block', 'A drawing app'],
          answer: 1,
        },
        {
          question: 'What is a "sprite" in Scratch?',
          options: ['A sound effect', 'A background image', 'A character or object you can program', 'A type of loop'],
          answer: 2,
        },
        {
          question: 'What does a "loop" do in coding?',
          options: ['Stops the program', 'Repeats instructions over and over', 'Changes colors', 'Plays music'],
          answer: 1,
        },
        {
          question: 'In Minecraft Education Edition, what tool lets you run code?',
          options: ['The Crafting Table', 'Code Builder', 'The Furnace', 'The Chest'],
          answer: 1,
        },
        {
          question: 'What does an "if-then" block do in coding?',
          options: [
            'Repeats forever',
            'Stops the sprite',
            'Does something only when a condition is true',
            'Changes the background',
          ],
          answer: 2,
        },
      ],
      application: [
        {
          question: 'To make a Scratch sprite move to the right, you would:',
          options: ['Change y by 10', 'Change x by 10', 'Play a sound', 'Hide the sprite'],
          answer: 1,
        },
        {
          question: 'You want a Scratch sprite to say "Hello!" when you click it. Which blocks do you use?',
          options: [
            '"When green flag clicked" + "Say Hello!"',
            '"When this sprite clicked" + "Say Hello!"',
            '"Repeat 10" + "Say Hello!"',
            '"If touching edge" + "Say Hello!"',
          ],
          answer: 1,
        },
        {
          question: 'In Minecraft, to make a chicken walk forward 5 steps using code, you would use:',
          options: [
            'A loop that repeats 5 times with a "move forward" command',
            'Place 5 blocks',
            'Open a chest 5 times',
            'Craft a compass',
          ],
          answer: 0,
        },
        {
          question: 'Your Scratch cat needs to bounce back when it hits the edge of the screen. Which block helps?',
          options: ['Change costume', 'If on edge, bounce', 'Play sound until done', 'Set size to 50'],
          answer: 1,
        },
        {
          question: 'To make a Scratch sprite repeat an animation 10 times, you use:',
          options: ['A "forever" loop', 'A "repeat 10" loop', 'An "if-then" block', 'A "stop all" block'],
          answer: 1,
        },
      ],
    },
  },
  {
    name: 'Robotics & Coding',
    ageRange: '6–12',
    difficulty: 'intermediate',

    color: '#f7971e',
    emoji: '🦾',
    tagline: 'Program a robot to move, sense, and solve challenges — then show it off on demo day!',
    desc: 'Learn block-based coding and program robots to move, use sensors, and complete team missions across 5 action-packed days.',
    images: [
      { softBg: 'linear-gradient(180deg, #fff7ed 0%, #fed7aa 100%)', icon: '💡', caption: 'Code with Blocks', desc: 'Learn robotics basics and snap together your first commands' },
      { softBg: 'linear-gradient(180deg, #fef9c3 0%, #fde68a 100%)', icon: '🤖', caption: 'Move & Sense', desc: 'Program movement, use sensors, and navigate obstacles' },
      { softBg: 'linear-gradient(180deg, #ffedd5 0%, #fdba74 100%)', icon: '🏁', caption: 'Mission & Demo Day', desc: 'Tackle team missions and present your robot to everyone' },
    ],
    featuredProject: {
      title: 'Demo Day Robot Showcase',
      desc: 'Program a robot to complete a real obstacle mission, then present it on demo day.',
      bullets: ['Block-Based Code', 'Sensor Commands', 'Team Mission', 'Live Demo'],
    },
    activityCopy: 'Watch students code their robots step by step — from basic moves to logic challenges — then cheer each other on during the final demo.',
    reassurance: [
      { title: 'No Experience Needed', desc: 'Perfect for first-time coders' },
      { title: 'Learn by Doing', desc: 'Every lesson is hands-on with real challenges' },
      { title: 'Team & Solo Missions', desc: 'Collaborate with friends and solve problems together' },
    ],
    interests: ['Robots', 'Building', 'Coding', 'Science', 'Teamwork'],
    quiz: {
      concept: [
        {
          question: 'What is a robot?',
          options: [
            'A cartoon character on TV',
            'A machine that can be programmed to do tasks automatically',
            'A toy that only moves when you push it',
            'A type of video game',
          ],
          answer: 1,
        },
        {
          question: 'What does it mean to "program" a robot?',
          options: [
            'Paint it a different color',
            'Give it a name',
            'Write a set of instructions that tell it what to do',
            'Fill it with batteries',
          ],
          answer: 2,
        },
        {
          question: 'What does a sensor on a robot do?',
          options: [
            'Makes the robot louder',
            'Helps the robot detect things in its environment, like walls or light',
            'Keeps the robot from getting wet',
            'Charges the robot faster',
          ],
          answer: 1,
        },
        {
          question: 'Which of these is an example of block-based coding?',
          options: [
            'Writing sentences in English',
            'Drawing pictures on paper',
            'Snapping together command blocks on a screen to create instructions',
            'Pressing buttons on a TV remote',
          ],
          answer: 2,
        },
        {
          question: 'Why do robots follow instructions in order, one step at a time?',
          options: [
            'Because they get confused easily',
            'Because computers run code in sequence and need clear steps to follow',
            'Because robots can only do one thing forever',
            'Because they are too slow to do two things at once',
          ],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'Your robot is supposed to stop when it hits a wall but it keeps going. What should you check first?',
          options: [
            'Change the color of the robot',
            'Check if the distance sensor is connected and working in the code',
            'Add more wheels to the robot',
            'Turn the robot off and leave it',
          ],
          answer: 1,
        },
        {
          question: 'You want your robot to turn left, then move forward. What order should your code blocks be in?',
          options: [
            'Move forward, then turn left',
            'Turn left, then move forward',
            'It does not matter what order you put them in',
            'Turn right, then move backward',
          ],
          answer: 1,
        },
        {
          question: 'Your robot keeps missing the finish line by a little bit. How can you fix this?',
          options: [
            'Build a completely new robot',
            'Adjust the distance or time value in your code so it travels farther',
            'Use a different color block',
            'Remove all the sensors',
          ],
          answer: 1,
        },
        {
          question: 'During demo day your robot stops halfway through the mission. What is the best first step?',
          options: [
            'Give up and watch someone else go',
            'Review your code to find where the instructions might have stopped or gone wrong',
            'Take the robot apart completely',
            'Ask a friend to push it the rest of the way',
          ],
          answer: 1,
        },
        {
          question: 'You want your robot to repeat a movement 5 times. Which coding idea helps you do that without writing the same block 5 times?',
          options: [
            'A sensor',
            'A loop',
            'A color change',
            'A battery swap',
          ],
          answer: 1,
        },
      ],
    },
  },
  {
    name: 'Code & Soccer Analytics',
    ageRange: '8–12',
    difficulty: 'beginner',

    color: '#f7971e',
    emoji: '⚽',
    tagline: 'Use Python to collect, visualize, and analyze real soccer data.',
    desc: 'Python meets soccer passion — collect, visualize & analyze real player & team data.',
    images: [
      { softBg: 'linear-gradient(180deg, #fff7ed 0%, #fed7aa 100%)', icon: '⚽', caption: 'Soccer Data', desc: 'Explore real player and match stats' },
      { softBg: 'linear-gradient(180deg, #fef9c3 0%, #fde68a 100%)', icon: '🐍', caption: 'Python Coding', desc: 'Write scripts to load and filter data' },
      { softBg: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 100%)', icon: '📊', caption: 'Charts & Visuals', desc: 'Plot goals, passes, and player rankings' },
    ],
    featuredProject: {
      title: 'Build Your Own Soccer Stats Dashboard',
      desc: 'Write Python code to load a dataset, filter top players, and create charts showing goals and assists.',
      bullets: ['Python Script', 'Data Table', 'Bar Chart'],
    },
    activityCopy: 'Try the kinds of data challenges and coding questions students work through in class.',
    reassurance: [
      { title: 'Beginner Friendly', desc: 'No coding experience needed' },
      { title: 'Real Data', desc: 'Work with actual soccer stats' },
      { title: 'Future Ready', desc: 'Data skills every career uses' },
    ],
    interests: ['Soccer', 'Coding', 'Data', 'Python', 'Sports'],
    quiz: {
      concept: [
        {
          question: 'What is data analytics?',
          options: ['Designing websites', 'Examining data to find patterns and answers', 'Playing video games', 'Drawing charts by hand'],
          answer: 1,
        },
        {
          question: 'What is Python?',
          options: ['A type of snake', 'A soccer position', 'A programming language used for data analysis', 'A spreadsheet app'],
          answer: 2,
        },
        {
          question: 'What is a "dataset"?',
          options: ['A soccer drill', 'A collection of organized information', 'A Python error message', 'A type of chart'],
          answer: 1,
        },
        {
          question: 'What does a bar chart show?',
          options: ['How fast players run', 'Comparisons between categories using rectangular bars', 'The location of players on the field', 'A list of team names'],
          answer: 1,
        },
        {
          question: 'What does "filtering" data mean?',
          options: ['Deleting all the data', 'Selecting only the rows that match certain conditions', 'Sorting data alphabetically', 'Adding new columns'],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'You want to find all players who scored more than 10 goals. What do you do?',
          options: ['Sort by player name', 'Filter the dataset where goals > 10', 'Delete all other columns', 'Create a pie chart'],
          answer: 1,
        },
        {
          question: 'To show which team has the most wins, which chart type is best?',
          options: ['A line chart', 'A scatter plot', 'A bar chart', 'A word cloud'],
          answer: 2,
        },
        {
          question: "Your Python script prints an error: \"NameError: name 'goals' is not defined\". What most likely happened?",
          options: ['The data file is missing', 'You used a variable before creating it', 'The chart is the wrong color', 'You forgot to save the file'],
          answer: 1,
        },
        {
          question: 'You have a list of match scores. To find the average goals per game, you would:',
          options: ['Count the number of rows', 'Add all goals and divide by the number of games', 'Sort the scores from highest to lowest', 'Filter for games with 0 goals'],
          answer: 1,
        },
        {
          question: 'You want to compare passing accuracy across 5 teams. What is the best approach?',
          options: ['Write their names on paper', 'Load the data into Python and plot a bar chart of passing accuracy per team', 'Delete teams with low accuracy', 'Use a loop to print each team name'],
          answer: 1,
        },
      ],
    },
  },
  {
    name: 'Game Builders: Roblox & 2D Games',
    ageRange: '8–12',
    difficulty: 'beginner',

    color: '#0f766e',
    emoji: '🕹️',
    tagline: 'Go beyond playing games — build your own and share them with the world!',
    desc: 'Design and code original games using Roblox Studio and 2D game tools. Add real mechanics, polish your levels, and walk away with a fully playable game in your portfolio.',
    images: [
      { softBg: 'linear-gradient(180deg, #ccfbf1 0%, #99f6e4 100%)', icon: '🗺️', caption: 'Design Your Game World', desc: 'Plan levels, place obstacles, and craft a game players want to explore' },
      { softBg: 'linear-gradient(180deg, #d1fae5 0%, #a7f3d0 100%)', icon: '⚙️', caption: 'Code the Mechanics', desc: 'Scores, lives, power-ups, win conditions — you control it all' },
      { softBg: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 100%)', icon: '🏆', caption: 'Publish & Show Off', desc: 'Release your game and have real players play it' },
    ],
    featuredProject: {
      title: 'Ship a Fully Playable Original Game',
      desc: 'Students design, code, test, and publish their own game — complete with levels, a score system, and a custom win screen — all in one week.',
      bullets: ['Game Design Doc', 'Playable Levels', 'Score & Lives System', 'Published Game Link'],
    },
    activityCopy: 'Try the kind of design and coding questions real game builders think through — give it your best shot!',
    reassurance: [
      { title: 'No Roblox Account Needed to Start', desc: 'We set everything up on day one together' },
      { title: 'Build a Real Game', desc: 'Not a tutorial — your original idea, your rules' },
      { title: 'Portfolio Ready', desc: 'Leave with a shareable link to show anyone' },
    ],
    interests: ['Gaming', 'Game Design', 'Coding', 'Creativity', 'Problem Solving'],
    quiz: {
      concept: [
        {
          question: 'What is a "game engine"?',
          options: [
            'The motor inside a gaming console',
            'A special tool that helps you build and run video games',
            'The graphics card in a computer',
            'An online store for buying games',
          ],
          answer: 1,
        },
        {
          question: 'What is a "game loop"?',
          options: [
            'A level that goes around in a circle',
            'The code that keeps running over and over to update the game every frame',
            'A cheat code that lets you replay a level',
            'A loop of music that plays in the background',
          ],
          answer: 1,
        },
        {
          question: 'What does "collision detection" mean in a game?',
          options: [
            'Finding errors in your code',
            'Detecting when two objects in the game touch or overlap each other',
            'Checking if your internet connection is working',
            'Making sure your game runs at high speed',
          ],
          answer: 1,
        },
        {
          question: 'What is a "variable" used for in game coding?',
          options: [
            'A type of enemy character',
            'A container that stores information like score, lives, or player speed',
            'The name of your game file',
            'A menu screen that changes colors',
          ],
          answer: 1,
        },
        {
          question: 'What does "playtesting" mean?',
          options: [
            'Watching someone else play a game on YouTube',
            'Buying a new game to test out',
            'Playing your own game to find problems and see what needs to be improved',
            'Writing the instructions for your game',
          ],
          answer: 2,
        },
      ],
      application: [
        {
          question: 'Your game gives players 10 points every time they collect a coin. Where should you store the current score?',
          options: [
            'In the backdrop image',
            'In a variable called "score" that updates each time a coin is collected',
            'In a sound file',
            'In the game title screen',
          ],
          answer: 1,
        },
        {
          question: 'Players say your first level is way too hard and they quit immediately. What should you do?',
          options: [
            'Delete the entire game and start over',
            'Ignore the feedback — it is their problem',
            'Playtest the level yourself and adjust the difficulty so it is easier at the start',
            'Add more enemies to make it even harder',
          ],
          answer: 2,
        },
        {
          question: 'Your game character falls through the floor instead of standing on it. What is the most likely issue?',
          options: [
            'The character sprite is the wrong color',
            'The collision detection between the character and the floor is missing or broken',
            'The background music is too loud',
            'The game needs a better title screen',
          ],
          answer: 1,
        },
        {
          question: 'You want your game to show "You Win!" when the player reaches the final flag. Which coding idea handles that?',
          options: [
            'A loop that repeats forever',
            'A collision check — when the player touches the flag, display the win screen',
            'A variable that stores the player\'s name',
            'A sound block that plays music',
          ],
          answer: 1,
        },
        {
          question: 'You finished your Roblox game and want friends at school to play it. What is the best next step?',
          options: [
            'Bring your computer to school and let one person play at a time',
            'Describe what the game looks like in a presentation',
            'Publish the game on Roblox so anyone with the link can play it online',
            'Save it only on your hard drive',
          ],
          answer: 2,
        },
      ],
    },
  },
  {
    name: '3D Game Design Studio',
    ageRange: '8–15',
    difficulty: 'intermediate',

    color: '#4338ca',
    emoji: '🔮',
    tagline: 'Level up to real game dev — build 3D worlds, sculpt assets, and ship a level like the pros.',
    desc: 'Use the same tools as real game studios to sculpt 3D environments, light dramatic scenes, and export a playable level that goes straight into your portfolio.',
    images: [
      { softBg: 'linear-gradient(135deg, #c7d2fe 0%, #a5b4fc 100%)', icon: '🌌', caption: 'Build Epic 3D Worlds', desc: 'Sculpt terrain, drop in assets, and design levels players want to explore' },
      { softBg: 'linear-gradient(135deg, #e9d5ff 0%, #d8b4fe 100%)', icon: '⚡', caption: 'Light & Atmosphere', desc: 'Add dynamic shadows, fog, and mood lighting like a real studio' },
      { softBg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', icon: '🚀', caption: 'Export a Playable Level', desc: 'Package your world into a real, walkable game scene' },
    ],
    featuredProject: {
      title: 'Design & Export a Fully Lit 3D Game Level',
      desc: 'Students model props, build a complete scene with terrain and atmospheric lighting, then export a playable level they can showcase in their portfolio.',
      bullets: ['3D Environment', 'Custom Props', 'Lighting & Mood', 'Exportable Level'],
    },
    activityCopy: 'Tackle the same design-thinking and 3D concept challenges students face in the studio — give it your best shot!',
    reassurance: [
      { title: 'Pro-Level Tools', desc: 'Work with software used by actual game studios' },
      { title: 'Portfolio Ready', desc: 'Walk away with a 3D level you can show anyone' },
      { title: 'Zero Experience Needed', desc: 'We teach every tool from the ground up' },
    ],
    interests: ['3D Design', 'Game Dev', 'Art', 'Technology', 'Architecture'],
    quiz: {
      concept: [
        {
          question: 'What does "3D" mean in game design?',
          options: [
            'A game that can only be played on 3 devices',
            'Objects that have width, height, and depth — so they look real and solid',
            'A game with exactly 3 levels',
            'A drawing style that uses only 3 colors',
          ],
          answer: 1,
        },
        {
          question: 'What is a "mesh" in 3D modeling?',
          options: [
            'The background sky in a 3D scene',
            'A net of connected points and edges that form the shape of a 3D object',
            'The code that makes a character move',
            'A type of 3D texture pattern',
          ],
          answer: 1,
        },
        {
          question: 'What does "texture mapping" do in 3D game design?',
          options: [
            'Moves an object around the scene',
            'Wraps an image around the surface of a 3D model to give it color and detail',
            'Adds physics so objects fall to the ground',
            'Connects two 3D objects together',
          ],
          answer: 1,
        },
        {
          question: 'What is "lighting" used for in a 3D game scene?',
          options: [
            'Making the game run faster',
            'Creating atmosphere, depth, and mood — and showing players where to look',
            'Storing game save files',
            'Connecting objects to a physics system',
          ],
          answer: 1,
        },
        {
          question: 'What is a "polygon count" and why does it matter?',
          options: [
            'The number of players in a multiplayer game',
            'The number of triangles that make up a 3D model — lower counts run faster on most devices',
            'How many colors are used in a texture',
            'The score a player earns by collecting polygons',
          ],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'Your 3D character looks blocky and rough. What is the best way to make it look smoother?',
          options: [
            'Change its color to white',
            'Add more polygons or use a smoothing modifier to refine the surface',
            'Scale it up to be much bigger',
            'Delete it and draw a 2D version instead',
          ],
          answer: 1,
        },
        {
          question: 'Your game scene looks flat and lifeless even though the models look good. What should you try first?',
          options: [
            'Delete half the models to reduce clutter',
            'Add directional lighting and shadows to give the scene depth and atmosphere',
            'Change the player speed in the code',
            'Export it and try again from scratch',
          ],
          answer: 1,
        },
        {
          question: 'You modeled a detailed tree with 50,000 polygons. Your scene needs 200 trees. What problem might this cause?',
          options: [
            'The trees will look too realistic',
            'The game will run slowly because too many polygons overload the graphics processor',
            'The trees will turn invisible',
            'The textures will disappear from the trees',
          ],
          answer: 1,
        },
        {
          question: 'A player says your dungeon level feels too bright and not scary at all. What is the best fix?',
          options: [
            'Add more enemies',
            'Reduce the ambient light intensity, use dark shadows, and add flickering torch lights',
            'Make the walls a darker color in the texture file',
            'Remove all the models from the scene',
          ],
          answer: 1,
        },
        {
          question: 'You finished your 3D level and want to share it with a friend who does not have the design software. What should you do?',
          options: [
            'Send them a screenshot and describe the rest',
            'Export the level to a format they can open or package it as a standalone playable scene',
            'Ask them to install the same software and send the raw project files',
            'Record a 10-second video of you spinning the camera',
          ],
          answer: 1,
        },
      ],
    },
  },
  {
    name: 'Build Your Website',
    ageRange: '10–16',
    difficulty: 'intermediate',

    color: '#0891b2',
    emoji: '💻',
    tagline: 'Turn a blank screen into a real website you can share with anyone.',
    desc: 'Design and launch your own website from scratch. Learn HTML, CSS & JavaScript through real projects.',
    images: [
      { softBg: 'linear-gradient(180deg, #cffafe 0%, #a5f3fc 100%)', icon: '🏗️', caption: 'Build with HTML', desc: 'Structure pages with headings, images, and links' },
      { softBg: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 100%)', icon: '🎨', caption: 'Style with CSS', desc: 'Add colors, fonts, and layouts that pop' },
      { softBg: 'linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%)', icon: '⚡', caption: 'Add Interactivity', desc: 'Use JavaScript to make buttons, menus & animations work' },
    ],
    featuredProject: {
      title: 'Launch a Personal Website',
      desc: 'Design and publish a real multi-page website about a topic you love — from scratch.',
      bullets: ['HTML Structure', 'CSS Styling', 'JavaScript Interactions', 'Live Published Site'],
    },
    activityCopy: 'Watch how students go from a blank file to a fully styled, interactive website they can share with friends and family.',
    reassurance: [
      { title: 'No Experience Needed', desc: 'Start from zero — we teach every step' },
      { title: 'Make It Yours', desc: 'Design a site about anything you love' },
      { title: 'Real Web Skills', desc: 'HTML, CSS & JS are used by pros worldwide' },
    ],
    interests: ['Design', 'Coding', 'Creativity', 'Technology', 'Storytelling'],
    quiz: {
      concept: [
        {
          question: 'What does HTML stand for?',
          options: [
            'How To Make Links',
            'HyperText Markup Language',
            'Highly Technical Machine Learning',
            'Home Tool Markup Loader',
          ],
          answer: 1,
        },
        {
          question: 'What is CSS mainly used for on a website?',
          options: [
            'Storing data in a database',
            'Making the website talk to a server',
            'Controlling the look and style of a webpage',
            'Writing the words and paragraphs on a page',
          ],
          answer: 2,
        },
        {
          question: 'Which HTML tag is used to create a clickable link?',
          options: [
            '<link>',
            '<button>',
            '<a>',
            '<p>',
          ],
          answer: 2,
        },
        {
          question: 'What does JavaScript let you do on a website?',
          options: [
            'Change the color of the browser icon',
            'Add interactive features like animations and responding to clicks',
            'Write text in different languages',
            'Store images on a hard drive',
          ],
          answer: 1,
        },
        {
          question: 'Which file type contains the structure of a webpage?',
          options: [
            '.css',
            '.js',
            '.html',
            '.png',
          ],
          answer: 2,
        },
      ],
      application: [
        {
          question: 'You want the title of your webpage to appear big at the top. Which HTML tag should you use?',
          options: [
            '<title>',
            '<p>',
            '<h1>',
            '<bold>',
          ],
          answer: 2,
        },
        {
          question: 'Your website text is hard to read because it is too small. Where do you fix this?',
          options: [
            'In the HTML file by adding more words',
            'In the CSS file by changing the font-size property',
            'In the JavaScript file by writing an alert',
            'In the browser settings menu',
          ],
          answer: 1,
        },
        {
          question: 'You want a button that shows a message when clicked. Which language handles that behavior?',
          options: [
            'HTML',
            'CSS',
            'JavaScript',
            'Markdown',
          ],
          answer: 2,
        },
        {
          question: 'You added an image tag but the image is not showing up. What is the most likely problem?',
          options: [
            'Images are not allowed in HTML',
            'The src path to the image file is wrong or missing',
            'You need JavaScript to display images',
            'The CSS is hiding every image by default',
          ],
          answer: 1,
        },
        {
          question: 'You want every page on your website to use the same colors and fonts. What is the best approach?',
          options: [
            'Copy and paste the styles into every HTML file separately',
            'Write a single CSS file and link it to all your HTML pages',
            'Use a different HTML file for each color',
            'Only JavaScript can set colors across multiple pages',
          ],
          answer: 1,
        },
      ],
    },
  },
  {
    name: 'Generative AI Creators Studio',
    ageRange: '10–18',
    difficulty: 'beginner',

    color: '#16a34a',
    emoji: '🎨',
    tagline: 'Beginner-friendly AI creativity for ages 10–18',
    desc: "Create AI art, write stories, make music, and build projects you'll be proud to share.",
    images: [
      { softBg: 'linear-gradient(180deg, #dff8e8 0%, #c7f0d6 100%)', icon: '🎨', caption: 'Create AI Art', desc: 'Posters, characters, and visual ideas' },
      { softBg: 'linear-gradient(180deg, #e7e4ff 0%, #d7d1ff 100%)', icon: '✍️', caption: 'Write AI Stories', desc: 'Prompts, comics, and creative writing' },
      { softBg: 'linear-gradient(180deg, #ffd9ea 0%, #ffc6df 100%)', icon: '🎵', caption: 'Make AI Music', desc: 'Beats, melodies, and sound ideas' },
    ],
    featuredProject: {
      title: 'Build Your Own AI Creative Showcase',
      desc: 'Students combine art, storytelling, and music into one fun project they can share in their portfolio.',
      bullets: ['AI Poster', 'Story Scene', 'Music Mood'],
    },
    activityCopy: 'Preview how students learn with guided concepts and hands-on creative challenges.',
    reassurance: [
      { title: 'Beginner Friendly', desc: 'No prior experience needed' },
      { title: 'Project Based', desc: 'Students create real work' },
      { title: 'Future Ready', desc: 'Creative AI skills for tomorrow' },
    ],
    interests: ['Art', 'Writing', 'Music', 'Creativity', 'Technology'],
    quiz: {
      concept: [
        {
          question: 'What does "generative AI" mean?',
          options: [
            'AI that generates electricity',
            'AI that creates new content like images, text, or music',
            'AI that only analyzes existing data',
            'AI-powered robots that move around',
          ],
          answer: 1,
        },
        {
          question: 'What is a "prompt" when working with generative AI?',
          options: [
            'A type of computer chip',
            'An instruction or description you give to the AI',
            'The speed at which AI processes data',
            'A warning message from the AI',
          ],
          answer: 1,
        },
        {
          question: 'What is a Large Language Model (LLM)?',
          options: [
            'A robot that speaks many languages',
            'A database of old books',
            'An AI trained on large amounts of text to understand and generate language',
            'A translation dictionary',
          ],
          answer: 2,
        },
        {
          question: 'What is "training data" in AI?',
          options: [
            'Exercises programmed into a robot',
            'The examples an AI learns from to improve its performance',
            'The hardware inside an AI computer',
            'A set of rules coded by a programmer',
          ],
          answer: 1,
        },
        {
          question: 'What makes generative AI different from traditional software?',
          options: [
            'It only runs on the internet',
            'It can create original outputs it was never explicitly programmed to produce',
            'It is faster than all other programs',
            'It can only work with numbers',
          ],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'You want to create an AI-generated illustration for your school project. Which tool is best suited?',
          options: [
            'A spreadsheet app',
            'An image generative AI like Adobe Firefly or DALL-E',
            'A word processor',
            'A weather app',
          ],
          answer: 1,
        },
        {
          question: 'You type: "a futuristic city at night, glowing neon, digital art style" into an AI tool. What is this text called?',
          options: [
            'A command line',
            'A search query',
            'A prompt',
            'A script',
          ],
          answer: 2,
        },
        {
          question: 'Your AI-generated image looks nothing like what you imagined. What should you do first?',
          options: [
            'Restart your computer',
            'Switch to a different AI tool permanently',
            'Refine your prompt with more specific details',
            'Accept the result and move on',
          ],
          answer: 2,
        },
        {
          question: 'Which of these is the most effective prompt for an AI story generator?',
          options: [
            '"story"',
            '"something cool"',
            '"Write a 3-sentence adventure about a young inventor who discovers a portal to the future"',
            '"I want a nice story please"',
          ],
          answer: 2,
        },
        {
          question: "You're using AI to compose music. Which set of details helps the AI the most?",
          options: [
            'The color of your bedroom walls',
            'Genre, mood, tempo, and instruments you want',
            'The date and your school name',
            'How many friends you have',
          ],
          answer: 1,
        },
      ],
    },
  },
  {
    name: 'Build Your First AI App',
    ageRange: '10–18',
    difficulty: 'intermediate',

    color: '#1d4ed8',
    emoji: '🐍',
    tagline: 'Go from zero to a deployed AI app — in one guided week.',
    desc: 'Use Python and real AI APIs to build something that works. Structured for students with little to no coding experience.',
    images: [
      { softBg: 'linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%)', icon: '🐍', caption: 'Code in Python', desc: 'Write scripts, functions, and logic from scratch' },
      { softBg: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 100%)', icon: '🤖', caption: 'Connect AI APIs', desc: 'Integrate real AI services into your app' },
      { softBg: 'linear-gradient(180deg, #ddd6fe 0%, #c4b5fd 100%)', icon: '🚀', caption: 'Deploy Your App', desc: 'Share your finished app with the world online' },
    ],
    featuredProject: {
      title: 'Launch a Working AI-Powered App',
      desc: 'Build and deploy a real app that uses an AI API to respond, generate, or classify.',
      bullets: ['Python Script', 'API Integration', 'Live Demo'],
    },
    activityCopy: 'See how students tackle real coding challenges and build AI-powered features step by step.',
    reassurance: [
      { title: 'Beginner Friendly', desc: 'No prior coding needed' },
      { title: 'Project Based', desc: 'Build and ship a real app' },
      { title: 'Future Ready', desc: 'In-demand Python + AI skills' },
    ],
    interests: ['Coding', 'AI', 'Apps', 'Technology', 'Problem Solving'],
    quiz: {
      concept: [
        {
          question: 'What is an API in the context of building AI apps?',
          options: [
            'A type of programming language',
            'An interface that lets your app communicate with an external AI service',
            'A special computer chip for AI',
            'A file storage system',
          ],
          answer: 1,
        },
        {
          question: 'What role does Python play in AI app development?',
          options: [
            'It is only used for video games',
            'It is a popular programming language with strong AI and data libraries',
            'It controls hardware like robots only',
            'It is exclusively used for web design',
          ],
          answer: 1,
        },
        {
          question: 'What does "deploying" an app mean?',
          options: [
            'Writing the first line of code',
            'Testing the app on your computer',
            'Making the app available for others to use online',
            'Drawing a diagram of the app',
          ],
          answer: 2,
        },
        {
          question: 'What is an API key used for?',
          options: [
            'Unlocking your keyboard',
            'Authenticating and authorizing your app to use a service',
            'Speeding up your internet connection',
            'Encrypting your Python files',
          ],
          answer: 1,
        },
        {
          question: 'Which of the following best describes a "model" in AI?',
          options: [
            'A physical display of a project',
            'A mathematical system trained on data to make predictions or generate outputs',
            'A template for designing websites',
            'A type of database',
          ],
          answer: 1,
        },
      ],
      application: [
        {
          question: 'You want your Python app to generate text answers using an AI service. What is your first step?',
          options: [
            'Design the app logo',
            'Sign up for an AI API and get an API key',
            'Buy a new computer',
            'Write the final deployment script',
          ],
          answer: 1,
        },
        {
          question: 'Your app returns an "unauthorized" error when calling the AI API. What is most likely the problem?',
          options: [
            'Your Python version is too old',
            'The AI service is offline forever',
            'Your API key is missing or incorrect in the code',
            'Your app has too many lines of code',
          ],
          answer: 2,
        },
        {
          question: 'You built a chatbot in Python that answers questions. Which step comes after testing it locally?',
          options: [
            'Delete all your code and start over',
            'Deploy it to a platform so others can access it',
            'Print the code on paper',
            'Save it only on a USB drive',
          ],
          answer: 1,
        },
        {
          question: 'You want to make your AI app respond differently based on what a user types. Which concept do you use?',
          options: [
            'Random number generation',
            'Static HTML pages',
            'User input handling and dynamic prompts',
            'Image compression',
          ],
          answer: 2,
        },
        {
          question: 'Which of these is the best way to keep your API key secure in your Python project?',
          options: [
            'Paste it directly into the main code file and share it on GitHub',
            'Store it in an environment variable or a secure config file excluded from version control',
            'Email it to yourself for easy access',
            'Write it on a sticky note next to your computer',
          ],
          answer: 1,
        },
      ],
    },
  },
  {
    name: 'AI Innovation',
    ageRange: '12–18',
    difficulty: 'advanced',

    color: '#312e81',
    emoji: '🤖',
    tagline: 'Design, build & pitch an original AI solution that makes a real difference.',
    desc: 'A challenge-based program for ambitious students who want to explore AI through problem solving, prototyping, and presenting.',
    images: [
      { softBg: 'linear-gradient(180deg, #e0e7ff 0%, #c7d2fe 100%)', icon: '💡', caption: 'Design Solutions', desc: 'Identify problems and sketch AI-powered ideas' },
      { softBg: 'linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%)', icon: '🌍', caption: 'Real-World Impact', desc: 'Tackle real challenges with AI technology' },
      { softBg: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)', icon: '🏆', caption: 'Pitch & Certify', desc: 'Present your idea and earn a blockchain certificate' },
    ],
    featuredProject: {
      title: 'Pitch an Original AI Solution',
      desc: 'Design, build, and present an AI-powered idea with real-world impact. Earn a blockchain certificate.',
      bullets: ['Problem Statement', 'AI Prototype', 'Pitch Deck'],
    },
    activityCopy: 'Experience the challenge questions and design thinking prompts used in the real program.',
    reassurance: [
      { title: 'Challenge Based', desc: 'Real-world problem solving' },
      { title: 'Project Based', desc: 'Build and present your idea' },
      { title: 'Future Ready', desc: 'AI + leadership + innovation' },
    ],
    interests: ['Innovation', 'AI', 'Entrepreneurship', 'Leadership', 'Technology'],
    quiz: {
      concept: [
        {
          question: 'What is artificial intelligence (AI)?',
          options: [
            'A robot that looks like a human',
            'Computer systems that can learn from data and make decisions',
            'A type of internet connection',
            'Software that only follows exact instructions',
          ],
          answer: 1,
        },
        {
          question: 'What does it mean to "train" an AI model?',
          options: [
            'Exercise the computer to make it faster',
            'Show the AI many examples so it learns patterns',
            'Write instructions for every possible answer',
            'Connect the AI to the internet',
          ],
          answer: 1,
        },
        {
          question: 'Which of these is an example of AI being used in real life?',
          options: [
            'A light switch turning on',
            'A calculator adding numbers',
            'A music app recommending songs you might like',
            'A fan spinning when it is hot',
          ],
          answer: 2,
        },
        {
          question: 'What is a "dataset" in AI?',
          options: [
            'A collection of examples the AI learns from',
            'A type of computer screen',
            'The speed of a processor',
            'A drawing tool in design software',
          ],
          answer: 0,
        },
        {
          question: 'Why is it important that an AI solution solves a real problem?',
          options: [
            'So it uses more data',
            'So it runs faster on newer hardware',
            'So it actually helps people and creates positive impact',
            'So it is easier to code',
          ],
          answer: 2,
        },
      ],
      application: [
        {
          question: 'You want to build an AI app that helps students find study resources. What is the first thing you should do?',
          options: [
            'Write all the code immediately',
            'Clearly define the problem and who will use it',
            'Buy the most expensive computer',
            'Search for the longest dataset online',
          ],
          answer: 1,
        },
        {
          question: 'You are presenting your AI project to judges. Your first sentence should be:',
          options: [
            '"We used Python 3.11 and TensorFlow."',
            '"Our project has 500 lines of code."',
            '"Our app helps elderly people remember to take medicine by sending smart reminders."',
            '"We spent three weeks building this."',
          ],
          answer: 2,
        },
        {
          question: 'Your AI app gives wrong answers sometimes. What is the most likely reason?',
          options: [
            'The screen is too bright',
            'The app was not trained on enough good examples',
            'The keyboard needs replacing',
            'The app has too many buttons',
          ],
          answer: 1,
        },
        {
          question: 'You want your AI solution to help farmers detect sick crops. What kind of data would you need?',
          options: [
            'Photos of healthy and sick crops labeled correctly',
            'Weather forecasts from 10 years ago',
            'A list of farmers\' names',
            'Satellite maps of cities',
          ],
          answer: 0,
        },
        {
          question: 'A friend says your AI project idea is not fair because it only works for one language. What should you do?',
          options: [
            'Ignore the feedback and submit anyway',
            'Add more animations to make it look better',
            'Consider expanding the training data to include more languages',
            'Rename the project',
          ],
          answer: 2,
        },
      ],
    },
  },
];

const MOCK_PLAYERS = [
  { rank: '🥇', name: 'AceCoderX',    xp: 480, level: 4 },
  { rank: '🥈', name: 'PixelNinja',   xp: 360, level: 3 },
  { rank: '🥉', name: 'ByteBlaster',  xp: 240, level: 2 },
];

const MOCK_ACHIEVEMENTS = [
  { emoji: '🏆', name: 'First Code' },
  { emoji: '🔥', name: 'Streak Master' },
  { emoji: '🎯', name: 'Quiz Pro' },
  { emoji: '🤖', name: 'AI Explorer' },
];

const DIFF_CLASSES = {
  beginner:     'k12-diff-beginner',
  intermediate: 'k12-diff-intermediate',
  advanced:     'k12-diff-advanced',
};

const DIFF_CHIP_CLASSES = {
  beginner:     'k12-chip--green',
  intermediate: 'k12-chip--blue',
  advanced:     'k12-chip--purple',
};

const toSlug = name => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function hexToRgb(hex) {
  const c = hex.replace('#', '');
  return `${parseInt(c.slice(0,2),16)}, ${parseInt(c.slice(2,4),16)}, ${parseInt(c.slice(4,6),16)}`;
}

function K12Preview({ onBack, initialCamp }) {
  const [campPreview, setCampPreview] = useState(null);
  const [quiz, setQuiz] = useState(null);

  const openPreview = (camp) => {
    setCampPreview(camp);
    setQuiz(null);
    history.replaceState(null, '', `#k12/${toSlug(camp.name)}`);
  };
  const closePreview = () => {
    setCampPreview(null);
    setQuiz(null);
    history.replaceState(null, '', '#k12');
  };

  // Open the matching camp modal when arriving via a deep link (e.g. #k12/generative-ai-creators-studio)
  useEffect(() => {
    if (initialCamp) {
      const camp = K12_CAMPS.find(c => toSlug(c.name) === initialCamp);
      if (camp) openPreview(camp);
    }
  }, []);

  const scrollToCamps = () => {
    const el = document.getElementById('k12-camps');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="k12-root">
      {/* ── TOP BAR ── */}
      <div className="k12-topbar">
        <button className="k12-back-btn" onClick={onBack}>
          ← All Programs
        </button>
        <img src="/logos/sv4u-logo.png" alt="SiliconValley4U" className="k12-topbar-logo" />
        <a
          href="https://www.siliconvalley4u.com/signup"
          target="_blank"
          rel="noopener noreferrer"
          className="k12-topbar-cta"
        >
          Enroll Now
        </a>
      </div>

      {/* ── HERO ── */}
      <section className="k12-hero">
        <div className="k12-hero-inner">
          <div className="k12-emoji-row">
            <span>🚀</span><span>🎮</span><span>🤖</span><span>🐍</span>
            <span>🎨</span><span>⛏️</span><span>💻</span><span>🦾</span>
          </div>
          <h1>Build Real Skills.<br />Ship Real Projects.<br />Level Up Your Future. 🚀</h1>
          <p className="k12-hero-sub">
            12 intensive programs for ages 6–18 — Python, AI &amp; Machine Learning, Robotics,
            Game Dev, Web Dev and more. Earn XP, level up, and build a real tech portfolio.
          </p>
          <div className="k12-hero-ctas">
            <button className="k12-hero-cta-explore" onClick={scrollToCamps}>
              👀 Preview Programs
            </button>
            <a
              href="https://www.siliconvalley4u.com/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="k12-hero-cta-primary"
            >
              Enroll in a Program 🚀
            </a>
            <button className="k12-hero-cta-ghost" onClick={onBack}>
              ← View All Programs
            </button>
          </div>
        </div>
      </section>

      {/* ── GAMIFICATION DASHBOARD MOCK ── */}
      <div className="k12-section-alt">
        <div className="k12-section">
          <p className="k12-section-label">Gamified Learning Experience</p>
          <h2 className="k12-section-title">It's Learning AND It's a Game 🎯</h2>
          <p className="k12-section-sub">
            Earn XP for every lesson, level up as you master topics, and compete
            on a real leaderboard with peers. Here's what your dashboard looks like:
          </p>

          <div className="k12-dash-mock">
            {/* Player header */}
            <div className="k12-dash-header">
              <div className="k12-dash-avatar">A</div>
              <div>
                <div className="k12-dash-player-name">AceCoderX</div>
                <span className="k12-dash-level">⭐ Level 4 Coder</span>
              </div>
            </div>

            {/* XP bar */}
            <div className="k12-xp-row">
              <span>240 XP earned</span>
              <span>360 XP for Level 5</span>
            </div>
            <div className="k12-xp-bar">
              <div className="k12-xp-fill" />
            </div>

            {/* Stats */}
            <div className="k12-dash-stats">
              <div className="k12-dash-stat">
                <span className="k12-dash-stat-emoji">🏕️</span>
                <span className="k12-dash-stat-val">3/12</span>
                <span className="k12-dash-stat-lbl">Programs Done</span>
              </div>
              <div className="k12-dash-stat">
                <span className="k12-dash-stat-emoji">🎯</span>
                <span className="k12-dash-stat-val">87%</span>
                <span className="k12-dash-stat-lbl">Accuracy</span>
              </div>
              <div className="k12-dash-stat">
                <span className="k12-dash-stat-emoji">🔥</span>
                <span className="k12-dash-stat-val">5</span>
                <span className="k12-dash-stat-lbl">Day Streak</span>
              </div>
            </div>

            {/* Achievements */}
            <div className="k12-achievement-row">
              {MOCK_ACHIEVEMENTS.map(a => (
                <div key={a.name} className="k12-ach-card">
                  <span className="k12-ach-emoji">{a.emoji}</span>
                  <span className="k12-ach-name">{a.name}</span>
                </div>
              ))}
            </div>

            {/* Mini leaderboard */}
            <div className="k12-leaderboard-mini">
              <div className="k12-lb-title">🏆 Leaderboard</div>
              {MOCK_PLAYERS.map(p => (
                <div key={p.name} className="k12-lb-row">
                  <span className="k12-lb-rank">{p.rank}</span>
                  <span className="k12-lb-name">{p.name}</span>
                  <span className="k12-lb-xp">Lv.{p.level} · {p.xp} XP</span>
                </div>
              ))}
            </div>

            <p className="k12-dash-caption">
              ✨ Your personalized learning dashboard on NexGenKlick — track XP, achievements &amp; progress
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: 36 }}>
            <a
              href="https://www.nexgenklick.ai/landing"
              target="_blank"
              rel="noopener noreferrer"
              className="k12-ngk-btn"
            >
              🏆 Build My Portfolio on NexGenKlick
            </a>
          </div>
        </div>
      </div>

      {/* ── CAMPS GRID ── */}
      <div id="k12-camps" className="k12-section">
        <p className="k12-section-label">12 Programs to Choose From</p>
        <h2 className="k12-section-title">Find Your Perfect Program</h2>
        <p className="k12-section-sub">
          Beginner to advanced, ages 6 to 18 — there's a program perfectly matched to every
          interest and skill level.
        </p>

        <div className="k12-camps-grid">
          {K12_CAMPS.map(camp => (
            <div key={camp.name} className="k12-camp-card">
              <div
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  height: 5,
                  background: camp.color,
                  borderRadius: '20px 20px 0 0',
                }}
              />
              <div className="k12-camp-top" style={{ marginTop: 4 }}>
                <span className="k12-camp-emoji">{camp.emoji}</span>
                <span className="k12-camp-age">Ages {camp.ageRange}</span>
              </div>
              <div className="k12-camp-name">{camp.name}</div>
              <p className="k12-camp-desc">{camp.desc}</p>
              <span className={`k12-camp-diff ${DIFF_CLASSES[camp.difficulty]}`}>
                {camp.difficulty}
              </span>
              <button className="k12-preview-btn" onClick={() => openPreview(camp)}>
                🎬 Preview
              </button>
            </div>
          ))}
        </div>

        <div className="k12-camps-cta">
          <a
            href="https://www.siliconvalley4u.com/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="k12-enroll-btn"
          >
            Enroll Now →
          </a>
        </div>
      </div>

      {/* ── CAMP PREVIEW MODAL ── */}
      {campPreview && (
        <div className="k12-modal-overlay" onClick={closePreview}>
          <div
          className="k12-modal"
          style={{ '--camp-color': campPreview.color, '--camp-rgb': hexToRgb(campPreview.color) }}
          onClick={e => e.stopPropagation()}
        >

            {/* 1. HEADER */}
            <div className="k12-course-top">
              <div className="k12-course-icon">{campPreview.emoji}</div>
              <div className="k12-modal-header-text">
                <h3 className="k12-modal-title">{campPreview.name}</h3>
                <div className="k12-meta-row">
                  <span className={`k12-chip ${DIFF_CHIP_CLASSES[campPreview.difficulty] || 'k12-chip--blue'}`}>
                    {campPreview.difficulty.charAt(0).toUpperCase() + campPreview.difficulty.slice(1)}
                  </span>
                  <span className="k12-chip k12-chip--blue">Ages {campPreview.ageRange}</span>
                  {campPreview.featuredProject && (
                    <span className="k12-chip k12-chip--pink">Project Based</span>
                  )}
                </div>
              </div>
              <button className="k12-modal-close" onClick={closePreview}>✕</button>
            </div>

            {/* ── BODY ── */}
            <div className="k12-course-body">

              {/* 2. HERO SUMMARY */}
              {campPreview.tagline && (
                <p className="k12-modal-hero">{campPreview.tagline}</p>
              )}

              {/* 3. WHAT STUDENTS WILL EXPLORE */}
              {campPreview.images && (
                <div className="k12-section">
                  <div className="k12-section-card">
                    <h4 className="k12-section-title">What Students Will Explore</h4>
                    <div className="k12-explore-grid">
                      {campPreview.images.map((img, i) => (
                        <div key={i} className="k12-explore-item" style={{ background: img.softBg }}>
                          <div className="k12-explore-emoji">{img.icon}</div>
                          <div>
                            <p className="k12-explore-h">{img.caption}</p>
                            {img.desc && <p className="k12-explore-p">{img.desc}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 4. FEATURED PROJECT */}
              {campPreview.featuredProject && (
                <div className="k12-section">
                  <div className="k12-project-card">
                    <h4 className="k12-section-title">Featured Project</h4>
                    <p className="k12-project-title">{campPreview.featuredProject.title}</p>
                    <p className="k12-project-copy">{campPreview.featuredProject.desc}</p>
                    {campPreview.featuredProject.bullets && (
                      <div className="k12-project-bullets">
                        {campPreview.featuredProject.bullets.map((b, i) => (
                          <span key={i} className="k12-mini-pill">{b}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 5. TRY A SAMPLE ACTIVITY */}
              {campPreview.quiz && !quiz && (
                <div className="k12-section">
                  <div className="k12-challenge-box">
                    <h4 className="k12-section-title">🧠 Try a Sample Activity</h4>
                    {campPreview.activityCopy && <p className="k12-challenge-copy">{campPreview.activityCopy}</p>}
                    <div className="k12-challenge-actions">
                      <button
                        className="k12-btn k12-btn--secondary"
                        onClick={() => setQuiz({ section: 'concept', idx: 0, selected: null, answers: [], done: false })}
                      >
                        Learn the Basics
                      </button>
                      <button
                        className="k12-btn k12-btn--primary"
                        onClick={() => setQuiz({ section: 'application', idx: 0, selected: null, answers: [], done: false })}
                      >
                        Create Something
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Quiz: active question */}
              {campPreview.quiz && quiz && !quiz.done && (() => {
                const questions = campPreview.quiz[quiz.section];
                const q = questions[quiz.idx];
                const revealed = quiz.selected !== null;
                const isRight = revealed && quiz.selected === q.answer;
                return (
                  <div className="k12-quiz-active">
                    <div className="k12-quiz-header">
                      <span className={`k12-quiz-type-badge k12-quiz-type-badge--${quiz.section}`}>
                        {quiz.section === 'concept' ? 'Basic Concepts Check' : 'Creative Challenge'}
                      </span>
                      <span className="k12-quiz-progress">Question {quiz.idx + 1} of {questions.length}</span>
                    </div>
                    <div className="k12-quiz-progress-bar">
                      <div
                        className={`k12-quiz-progress-fill k12-quiz-progress-fill--${quiz.section}`}
                        style={{ width: `${((quiz.idx + 1) / questions.length) * 100}%` }}
                      />
                    </div>
                    <p className="k12-quiz-question">{q.question}</p>
                    <div className="k12-quiz-options">
                      {q.options.map((opt, i) => {
                        const isCorrect = i === q.answer;
                        let cls = 'k12-quiz-option';
                        if (revealed && isCorrect) cls += ' correct';
                        else if (revealed && quiz.selected === i) cls += ' wrong';
                        return (
                          <button
                            key={i}
                            className={cls}
                            onClick={() => quiz.selected === null && setQuiz({ ...quiz, selected: i })}
                          >
                            <span className="k12-quiz-radio" />
                            <span>{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="k12-quiz-footer">
                      <button className="k12-quiz-back-btn" onClick={() => setQuiz(null)}>← Back</button>
                      {quiz.selected !== null && (
                        <button
                          className={`k12-quiz-next-btn k12-quiz-next-btn--${quiz.section}`}
                          onClick={() => {
                            const newAnswers = [...quiz.answers, quiz.selected];
                            if (quiz.idx + 1 < questions.length) {
                              setQuiz({ ...quiz, idx: quiz.idx + 1, selected: null, answers: newAnswers });
                            } else {
                              setQuiz({ ...quiz, done: true, answers: newAnswers });
                            }
                          }}
                        >
                          {quiz.idx + 1 < questions.length ? 'Next →' : 'See Results'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Quiz: results */}
              {campPreview.quiz && quiz && quiz.done && (() => {
                const questions = campPreview.quiz[quiz.section];
                const score = quiz.answers.filter((ans, i) => ans === questions[i].answer).length;
                return (
                  <div className="k12-quiz-results">
                    <div className="k12-quiz-score-emoji">
                      {score === 5 ? '🎉' : score >= 3 ? '👍' : '💪'}
                    </div>
                    <p className="k12-quiz-score-text">{score} / {questions.length} correct</p>
                    <p className="k12-quiz-score-msg">
                      {score === 5
                        ? "Perfect score! You're ready for this program!"
                        : score >= 3
                        ? 'Great job! Come to the program to level up even more!'
                        : 'Good effort! This program will teach you everything!'}
                    </p>
                    <button className="k12-btn k12-btn--secondary" onClick={() => setQuiz(null)}>
                      ← Try Another Section
                    </button>
                  </div>
                );
              })()}

              {/* 6. WHY FAMILIES LIKE IT */}
              {campPreview.reassurance && (
                <div className="k12-section">
                  <h4 className="k12-section-title">Why Families Like It</h4>
                  <div className="k12-trust-row">
                    {campPreview.reassurance.map((r, i) => (
                      <div key={i} className="k12-trust-item">
                        <strong>{typeof r === 'string' ? r : r.title}</strong>
                        {r.desc && <span>{r.desc}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 6. CTA FOOTER */}
              <div className="k12-modal-cta">
                <a
                  href="https://www.siliconvalley4u.com/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="k12-btn k12-btn--secondary"
                >
                  View Schedule
                </a>
                <a
                  href="https://www.siliconvalley4u.com/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="k12-btn k12-btn--primary"
                >
                  Register Now
                </a>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── FINAL CTA BANNER ── */}
      <section className="k12-cta-banner">
        <h2>Ready to Explore?</h2>
        <p>Join a class and start creating with AI.</p>
        <div className="k12-cta-banner-btns">
          <a
            href="https://www.siliconvalley4u.com/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="k12-cta-main-btn"
          >
            View Schedule
          </a>
          <a
            href="https://www.siliconvalley4u.com/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="k12-cta-secondary-btn"
          >
            Register Now
          </a>
        </div>
      </section>
    </div>
  );
}

export default K12Preview;
